import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CalendarMonthViewDay, CalendarView } from 'angular-calendar';
import dayjs from 'dayjs';
import { CalendarViewTypes } from '../../config/calendar-view';
import { ACCELERATION_EVENT, HOLIDAY_EVENT, PUPIL_EVENT, UNASSIGNED_EVENT } from '../../config/events';
import { ROLES, ROLEVALUES } from '../../enum/roles.enum';
import { CalendarDayEvent, SchoolCalendar } from '../../interface/calendar';
import { ClassroomService } from './../../services/classroom.service';

import { User } from '../../interface/user';
import { AuthService } from '../../services/auth.service';
import { CalendarService } from '../../services/calendar.service';

import { NbPopoverDirective } from '@nebular/theme';
import { NgxSpinnerService } from 'ngx-spinner';
import { takeUntil } from 'rxjs';
import { AnalyticsService } from '../../@core/utils';
import { DummyUserIds } from '../../enum/dummy-user';
import { ClearObservableHelper } from '../../helpers/ClearObservableHelper';
import { LessonPlanService } from '../../services/lesson-plan.service';
import { PacingPlanService } from '../../services/pacing-plan.service';
import { StorageService } from '../../services/storage.service';
import { TeacherLessonPlanService } from '../../services/teacher-lesson-plan.service';
import { ToasterService } from '../../services/toaster.service';
import { UtilService } from '../../services/util.service';

@Component({
  selector: 'ngx-pacing-plans-month-view',
  templateUrl: './pacing-plan-month-view.component.html',
  styleUrls: ['./pacing-plan-month-view.component.scss'],
})
export class PacingPlansMonthViewComponent extends ClearObservableHelper implements OnInit {
  @ViewChild(NbPopoverDirective)
  standardsListPopoverTemplate: NbPopoverDirective;

  calendarViewTypes = Object.values(CalendarViewTypes);
  @Input() view: string = '';
  CalendarView = CalendarView;
  calendarViewType = CalendarView.Month;
  user: User | any;
  viewDate: Date = new Date();
  activeDayIsOpen: boolean = true;
  currentCalendar: SchoolCalendar[];
  events: CalendarDayEvent[] = [];
  loading = false;
  @Output() newItemEvent = new EventEmitter<string>();
  @Output() currentDate = new EventEmitter<any>();
  @ViewChild('pacingPlanViaDialog') pacingPlanViaDialog: TemplateRef<any>;
  @Output() closeDialogModal = new EventEmitter<boolean>();

  private dates = [];
  isCalendarEnd = false;
  isCalendarStart = false;
  isModalView = true;
  @Input() classroom;
  @Input() isModal: boolean;
  @Input() isTeacherCalendar;
  @Input() isSuperAdminViewUser: User;
  @Input() schoolCalendarEvents: SchoolCalendar[];
  @Input() currentUser: User;
  testAndReviewDays = [];
  @Output() currentLessonPlan = new EventEmitter<any>();
  globalPacingPlanEvents: any[] = [];
  teacherLessonPlanEvents: any = [];
  manualGlobalEvents;
  databaseEvents: any = [];
  deletedDays = [];
  private deletedTestDays;
  private deletedReviewDays;
  private isTeacherOnly = false;
  subjectHoursData;
  teacherOnlyAutoPopulateData;
  // isLoading = false;
  updatedPacingPlanEvents = []; // Updated pacing plan events from teacher only onboarding
  updatedTestAndReviewDays = []; // Updated test and review days from teacher only onboarding
  currentSelected: SchoolCalendar[];
  constructor(
    private storageService: StorageService,
    private authService: AuthService,
    public calendarService: CalendarService,
    private pacingPlanService: PacingPlanService,
    public dialog: MatDialog,
    private toasterService: ToasterService,
    public router: Router,
    private lessonPlanService: LessonPlanService,
    public analyticsService: AnalyticsService,
    private spinner?: NgxSpinnerService,
    private teacherLessonPlanService?: TeacherLessonPlanService,
    private utilService?: UtilService,
    private classroomService?: ClassroomService,
  ) {
    super();
  }

  async ngOnInit() {
    try {
      const user = this.storageService.getItem('user');

      this.loading = true;
      // this.spinner.show();
      this.isTeacherCalendar && this.isTeacherCalendar.id
        ? (this.user = this.isTeacherCalendar)
        : this.isSuperAdminViewUser && this.isSuperAdminViewUser.id
          ? (this.user = this.isSuperAdminViewUser)
          : (this.user = this.currentUser ? this.currentUser : await this.authService.userById(user.id));

      this.isTeacherOnly = this.user.role_id === ROLEVALUES.TEACHER_ONLY ? true : false;
      // for multi subjects classrooms
      await this.getSubjectHoursData();
      await this.setInitialViewPercondition();

      this.loading = false;
      this.pacingPlanService.isOpen.emit(true);

      this.pacingPlanService.isPushed.subscribe(async (x) => {
        if (x) {
          await this.setInitialViewPercondition();
          this.events = await this.updateEventsData();
          this.spinner.hide();
        }
      });

      this.storageService.setSelectedSchoolsCalendarToLocalStorage
        .pipe(takeUntil(this.destroy$))
        .subscribe(async (res) => {
          if (res) {
            await this.setInitialViewPercondition();
            this.events = await this.updateEventsData();
          }
        });

      this.pacingPlanService.changeClassroom.subscribe(async (x: any) => {
        if (x) {
          this.classroom = x;
          await this.setInitialViewPercondition();
          this.events = await this.updateEventsData();
          this.pacingPlanService.isOpen.emit(true);
          this.teacherOnlyAutoPopulateData = [];
          this.spinner.hide();
        }
      });

      this.pacingPlanService.currentSubject.subscribe(async (x) => {
        if (x) {
          this.classroom['current_subject_id'] = x;
          await this.setInitialViewPercondition();
          this.events = await this.updateEventsData();
          this.spinner.hide();
        }
      });

      this.spinner.hide();
    } catch (error) {
      this.pacingPlanService.isOpen.emit(true);
      this.spinner.hide();
      this.loading = false;
    }
  }

  /**
   * @description Method is used to get subject hours data for the particular user
   * @param void
   * @returns Promise<void>
   */
  private async getSubjectHoursData(): Promise<void> {
    const subjectHoursBody = {
      user_id: this.classroom.user_id ? this.classroom.user_id : this.user.id,
      subject_id: this.classroom.current_subject_id,
      grade_id: this.classroom.grades_id,
      day: this.utilService.getWeekDayString(this.viewDate.getDay()).toLowerCase(),
    };
    if (this.user?.teachingInfo?.schoolBellScheduleId && this.classroom.user_id === this.user.id)
      subjectHoursBody['school_bell_shedules_id'] = this.user?.teachingInfo?.schoolBellScheduleId;

    if (this.user.is_trial_user) {
      // Set the dummy teacher ID to fetch the subject hours
      subjectHoursBody['user_id'] = this.user?.teachingInfo?.dummyUserId ?? DummyUserIds.TEACHER;
    }
    this.subjectHoursData = await this.teacherLessonPlanService.getTeacherSubjectHoursByUserId(subjectHoursBody);
  }

  async getLessonPlanEvents() {
    try {
      const params = {
        subject_id: this.classroom.current_subject_id,
        grades_id: this.classroom.grades_id,
        user_id: this.classroom.user_id ? this.classroom.user_id : this.user.id,
        classroom_id: this.classroom?.id,
      };

      if (this.classroom?.is_dummy) {
        params['is_dummy'] = this.classroom.is_dummy;
      }

      this.teacherLessonPlanEvents = await this.lessonPlanService.getTeacherLessonPlanBySubjectAndGradesId(params);
      this.pacingPlanService.teacherLessonPlanEvents = this.teacherLessonPlanEvents;
    } catch (error) {
      // console.log(error)
    }
  }

  async setInitialViewPercondition() {
    try {
      if (this.user && this.user.role_id !== 19) {
        await this.getCurrentCalendar();
      }

      if (this.isTeacherCalendar && this.isTeacherCalendar.id) {
        this.view = 'Month';
        this.isModalView = false;
        this.isModal = true;
      } else {
        this.isModalView = this.classroom && this.classroom.id ? true : false;
      }
      await this.getDataUsingView();
    } catch (error) {
      // console.log(error);
    }
  }

  async getDataUsingView() {
    try {
      const date: any = this.currentCalendar[0]?.start;
      this.viewDate = date ? (new Date().setHours(0, 0, 0, 0) < date ? new Date(date) : new Date()) : new Date();

      if (this.currentCalendar && this.currentCalendar.length > 0) {
        this.navigateCalendar();
      }

      if (this.isModal) {
        if (this.pacingPlanService?.databaseEvents) {
          this.databaseEvents = this.pacingPlanService.databaseEvents;
        }

        this.pacingPlanService?.showPacingPlanDates && this.pacingPlanService?.showPacingPlanDates.length > 0
          ? (this.dates = this.pacingPlanService?.showPacingPlanDates)
          : await this.getDates();

        if (this.pacingPlanService?.testAndReviewDays?.length > 0) {
          this.testAndReviewDays = this.pacingPlanService.testAndReviewDays;
        } else {
          await this.getTestAndReviewDays();
        }
        if (this.pacingPlanService?.pacingPlanEvents && this.pacingPlanService?.pacingPlanEvents.length > 0) {
          this.globalPacingPlanEvents = this.pacingPlanService?.pacingPlanEvents;
        } else {
          await this.getGlobalPacingPlans();
        }
      } else {
        await this.getGlobalPacingPlans();
      }

      await this.getLessonPlanEvents();
    } catch (error) {
      console.log(error);
    }
  }

  // async getGlobalPacingPlans() {
  //   try {
  //     // this.spinner.show();
  //     const params = {
  //       subject_id: this.classroom.current_subject_id,
  //       grades_id: this.classroom.grades_id,
  //     };
  //     this.databaseEvents = this.classroom.isManual
  //       ? await this.pacingPlanService.getCustomEventsAndPacingPlansBySubjectId({
  //           subject_id: this.classroom.current_subject_id,
  //           classroom_id: this.classroom.id,
  //           grades_id: this.classroom.isManual ? '' : this.classroom.grades_id,
  //         })
  //       : await this.pacingPlanService.getGlobalPacingPlanBySubjectAndGradeId(params);
  //     const body = {
  //       subject_id: this.classroom.current_subject_id,
  //       grades_id: this.classroom.grades_id,
  //       user_id: this.user?.id,
  //       classroom_id: this.classroom.id,
  //     };
  //     const customPacingPlanEvents = await this.pacingPlanService.getCustomGlobalPacingPlan(body);
  //     await this.getTestAndReviewDays();
  //     await this.getDates();

  //     if (this.classroom.isManual) {
  //       this.globalPacingPlanEvents = this.databaseEvents.manual_pacing_plans.map((x) => {
  //         if (x.global_pacing_plan_id) {
  //           // If global_pacing_plan_id exists, modify global_pacing_plan
  //           return {
  //             ...x.global_pacing_plan,
  //             created_at: x.created_at,
  //             start_date: x.start_date,
  //             end_date: x.end_date,
  //           };
  //         } else {
  //           // Otherwise, modify teacher_custom_pacing_plans
  //           return {
  //             ...x.teacher_custom_pacing_plans,
  //             created_at: x.created_at,
  //             start_date: x.start_date,
  //             end_date: x.end_date,
  //           };
  //         }
  //       });
  //     } else {
  //       this.globalPacingPlanEvents = this.databaseEvents;
  //     }

  //     this.globalPacingPlanEvents = [...this.globalPacingPlanEvents, ...customPacingPlanEvents];
  //     // teacher-only auto populate classroom

  //     // currently commented out because we don't want to shift dates
  //     // if (this.user.isTeacherOnlyOnBoarded && this.classroom.isManual) {
  //     //   // Create a new array to store sorted records from both arrays
  //     //   const sortedPacingPlanEvents = [
  //     //     ...this.globalPacingPlanEvents.map((event) => ({
  //     //       ...event,
  //     //       date: new Date(event.start || event.start_date), // Ensuring valid date
  //     //       type: 'pacing',
  //     //     })),
  //     //     ...this.testAndReviewDays.map((event) => ({
  //     //       ...event,
  //     //       date: event.start ? new Date(event.start) : new Date(), // Use start date or fallback to today
  //     //       type: 'test-review',
  //     //     })),
  //     //   ].sort((a, b) => a.date.getTime() - b.date.getTime());

  //     //   const updatedEvents = await this.pacingPlanService.assignDatesToSortedEvents(
  //     //     this.dates,
  //     //     sortedPacingPlanEvents,
  //     //   );
  //     //   this.updatedPacingPlanEvents = updatedEvents.filter((event) => event.type === 'pacing');
  //     //   this.updatedTestAndReviewDays = updatedEvents.filter((event) => event.type === 'test-review');

  //     //   // Output the results
  //     //   console.log('Updated Pacing Plan Events:', this.updatedPacingPlanEvents);
  //     //   console.log('Updated Test and Review Days:', this.updatedTestAndReviewDays);

  //     //   this.globalPacingPlanEvents = this.updatedPacingPlanEvents;
  //     //   this.testAndReviewDays = this.updatedTestAndReviewDays;
  //     //   await this.updateisTeacherOnlyOnBoardedFlag();
  //     // } else {
  //     await this.setDateToGlobalPacingPlanEvents(this.databaseEvents.manual_pacing_plans);
  //     // }

  //     await this.updatePacingPlansAsPerPushedPlans();

  //     this.events = await this.updateEventsData();
  //     this.pacingPlanService.pacingPlanEvents = this.globalPacingPlanEvents;
  //     this.pacingPlanService.databaseEvents = this.databaseEvents;
  //     this.pacingPlanService.testAndReviewDays = this.testAndReviewDays;
  //     // this.spinner.hide();
  //   } catch (error) {
  //     console.log(error);
  //   }
  // }

  async getGlobalPacingPlans() {
    try {
      // this.spinner.show();
      const params = {
        subject_id: this.classroom.current_subject_id,
        grades_id: this.classroom.grades_id,
      };
      this.databaseEvents = this.classroom.isManual
        ? await this.pacingPlanService.getCustomEventsAndPacingPlansBySubjectId({
            subject_id: this.classroom.current_subject_id,
            classroom_id: this.classroom.id,
            grades_id: this.classroom.isManual ? '' : this.classroom.grades_id,
          })
        : await this.pacingPlanService.getGlobalPacingPlanBySubjectAndGradeId(params);

      const body = {
        subject_id: this.classroom.current_subject_id,
        grades_id: this.classroom.grades_id,
        user_id: this.user?.id,
        classroom_id: this.classroom?.id,
      };

      const customPacingPlanEvents = await this.pacingPlanService.getCustomGlobalPacingPlan(body);
      await this.getTestAndReviewDays();
      await this.getDates();

      // ** old code **
      // if (this.currentClassroom.isManual) {
      //   this.globalPacingPlanEvents = this.databaseEvents.manual_pacing_plans.map((x) => {
      //     if (x.global_pacing_plan_id) {
      //       // If global_pacing_plan_id exists, modify global_pacing_plan
      //       return {
      //         ...x.global_pacing_plan,
      //         created_at: x.created_at,
      //         start_date: x.start_date,
      //         end_date: x.end_date,
      //       };
      //     } else {
      //       // Otherwise, modify teacher_custom_pacing_plans
      //       return {
      //         ...x.teacher_custom_pacing_plans,
      //         created_at: x.created_at,
      //         start_date: x.start_date,
      //         end_date: x.end_date,
      //       };
      //     }
      //   });

      // } else {
      //   this.globalPacingPlanEvents = this.databaseEvents;
      // }

      const currentlyAddedEvent = this.storageService.getItem('currentlyAddedEvent') || [];
      const currentSubjectId = this.classroom.current_subject_id;

      if (this.classroom.isManual) {
        const dbGlobalPacingPlanEvents = this.databaseEvents.manual_pacing_plans.map((x: any) => {
          const base = x.global_pacing_plan_id ? x.global_pacing_plan : x.teacher_custom_pacing_plans;
          return {
            ...base,
            id: base?.id,
            created_at: x.created_at,
            start_date: x.start_date,
            end_date: x.end_date,
          };
        });

        const addedGlobalEvents = currentlyAddedEvent
          .filter(
            (x: any) =>
              (x.global_pacing_plan || x.teacher_custom_pacing_plans) &&
              (x.global_pacing_plan?.subject_id ||
                x.global_pacing_plan?.subjectId ||
                x.teacher_custom_pacing_plans?.subject_id ||
                x.teacher_custom_pacing_plans?.subjectId) === currentSubjectId,
          )
          .map((x: any) => {
            const base = x.global_pacing_plan || x.teacher_custom_pacing_plans;
            return {
              ...base,
              id: base.id || x.global_pacing_plan?.teacher_custom_pacing_plans_id,
              created_at: x.created_at || new Date(),
              start_date: new Date(x.start),
              end_date: new Date(x.end),
            };
          });

        this.globalPacingPlanEvents = [...dbGlobalPacingPlanEvents, ...addedGlobalEvents];
        this.databaseEvents.manual_pacing_plans = this.globalPacingPlanEvents;
      } else {
        const dbEvents = this.databaseEvents;
        const addedGlobalEvents = currentlyAddedEvent
          .filter(
            (x: any) => x?.pacing_plan && (x.pacing_plan.subject_id || x.pacing_plan.subjectId) === currentSubjectId,
          )
          .map((x: any) => ({
            ...x.pacing_plan,
            id: x.pacing_plan.id || x.pacing_plan.teacher_custom_pacing_plans_id,
            created_at: x.created_at || new Date(),
            start_date: new Date(x.start),
            end_date: new Date(x.end),
            localStorageAutoPacingPlanData: true,
          }));
        this.globalPacingPlanEvents = [...dbEvents, ...addedGlobalEvents];
      }

      this.globalPacingPlanEvents = [...this.globalPacingPlanEvents, ...customPacingPlanEvents];

      await this.setDateToGlobalPacingPlanEvents(this.databaseEvents.manual_pacing_plans);
      await this.updatePacingPlansAsPerPushedPlans();

      this.events = await this.updateEventsData();
      this.pacingPlanService.pacingPlanEvents = this.globalPacingPlanEvents;
      this.pacingPlanService.databaseEvents = this.databaseEvents;
      this.pacingPlanService.testAndReviewDays = this.testAndReviewDays;
      // this.spinner.hide();
    } catch (error) {
      console.log(error);
      this.spinner.hide();
    }
  }

  async updateisTeacherOnlyOnBoardedFlag() {
    try {
      const body = {
        user_id: this.user.id,
        school_id: this.user.school_id,
        role_id: this.user.role_id,
        isTeacherOnlyOnBoarded: false,
      };
      const response: any = await this.authService.mergeTeacherOnlyData(body);
      if (response) {
        const updatedData = await this.authService.updateUser(response['id'], response, true);
        const localStorageData = this.storageService.getItem('user');
        this.storageService.removeItem('user');
        this.storageService.setItem('user', {
          authToken: localStorageData?.authToken,
          user: updatedData,
        });
      }
    } catch (error) {
      console.log(error);
    }
  }

  async checkDeletedStandardAndUpdate() {
    const body = {
      user_id: this.user.id,
      grades_id: this.classroom.grades_id,
      subject_id: this.classroom.current_subject_id,
      classroom_id: this.classroom?.id,
    };

    const response = await this.pacingPlanService.getDeletedStandardPerClassroomSubject(body);

    if (response && response.length > 0) {
      this.globalPacingPlanEvents = this.globalPacingPlanEvents.filter((item: any) => {
        return (
          item.multiple === true ||
          !response.some((deletedItem: any) => {
            return (
              (new Date(deletedItem.date).toDateString() === new Date(item.start || item.start_date).toDateString() &&
                item.id === deletedItem.section_id) ||
              item.multiple === true
            );
          })
        );
      });

      // Added later to resolve the custom pacing plan deletion issue
      this.globalPacingPlanEvents = this.globalPacingPlanEvents.filter((item: any) => {
        return !response.some((deletedItem: any) => {
          return (
            new Date(deletedItem.date).toDateString() === new Date(item.start || item.start_date).toDateString() &&
            item.id === deletedItem.section_id
          );
        });
      });
    }
    // if (this.classroom.isManual) {
    //   if (response && response.length > 0) {
    //     this.globalPacingPlanEvents = this.globalPacingPlanEvents.filter(
    //       (item: any) =>
    //         item.multiple === true ||
    //         !response.some(
    //           (deletedItem: any) =>
    //             new Date(deletedItem.id) === new Date(item.id)
    //         )
    //     );
    //   }
    // } else {
    //   if (response && response.length > 0) {
    //     this.globalPacingPlanEvents = this.globalPacingPlanEvents.filter(
    //       (item: any) => {
    //         return (
    //           item.multiple === true ||
    //           !response.some((deletedItem: any) => {
    //             // if (item.multiple) {
    //             return (
    //               (new Date(deletedItem.date).toDateString() ===
    //                 new Date(item.start || item.start_date).toDateString() &&
    //                 item.id === deletedItem.section_id) ||
    //               item.multiple === true
    //             );
    //             // } else {
    //             // return (
    //             //   new Date(deletedItem.date).toDateString() ===
    //             //   new Date(item.start).toDateString()
    //             // );
    //             // }
    //           })
    //         );
    //       }
    //     );
    //   }
    // }
  }

  // functionality of pushed or custom added pacing plans to the calendar
  async updatePacingPlansAsPerPushedPlans(): Promise<void> {
    try {
      let response: any = await this.getTeacherPacingPlans();
      response = response?.sort((a: any, b: any) => {
        return a.created_at > b.created_at ? 1 : -1;
      });

      if (response && response.length > 0) {
        for (const iterator of response) {
          // to check if pacing plan is custom added after deletion of standard
          if (iterator.isDeleted) {
            if (iterator?.extras?.isMultiple) {
              await this.customAddedStandards(iterator, true);
            } else {
              await this.customAddedStandards(iterator);
            }
          }
          // if the pacing plan is pushed
          else {
            // forward all the pacing plans from the current date to next dates
            await this.pushPlansToNextDate(iterator);

            if (iterator?.reason === 'Reteach Yesterday’s Standard') {
              await this.reteachYesterdayStandard(iterator);
            } else if (iterator?.reason === 'Reteach Standard of Choice') {
              await this.reteachStandardofChoice(iterator, iterator?.lesson_plan_pacing_id, false);
            } else {
              await this.reteachStandardofChoice(iterator, iterator.custom_global_pacing_plan_id, true);
            }
          }
        }
      }

      await this.checkDeletedStandardAndUpdate();
    } catch (error) {
      console.log(error);
    }
  }

  // functionality of custom added standard by deleting particular standard
  async customAddedStandards(iterator: any, multiple = false) {
    const globalPacingIndex = this.globalPacingPlanEvents.findIndex(
      (x: any) => new Date(x.start).toDateString() === new Date(iterator.start).toDateString(),
    );

    if (!multiple && globalPacingIndex !== -1) {
      this.globalPacingPlanEvents.splice(globalPacingIndex, 1);
    }
    if (iterator.reason && iterator.reason !== '') {
      this.testAndReviewDays.push({
        start: new Date(iterator.start),
        end: new Date(iterator.end),
        isDay: true,
        type: iterator.reason === 'test-day' ? 'Test Day' : 'Review Day',
      });
    } else {
      const newStandard = iterator.pacing_plan ? iterator.pacing_plan : iterator.teacher_custom_pacing_plans;
      // Added if condition to remove newStandard is undefined error
      if (newStandard) {
        newStandard['start'] = iterator.start;
        newStandard['end'] = iterator.end;
        newStandard['subject_id'] = iterator.subject_id;
        newStandard['grades_id'] = iterator.grades_id;
        newStandard['is_teacher_custom_pacing_plan'] = iterator.teacher_custom_pacing_plans ? true : false;
      }
      await this.pushStandardToGlobalEvents(newStandard, multiple);
    }
  }

  // for standard of choice push
  async reteachStandardofChoice(iterator: any, pacingPlanId: any, isCustom?) {
    try {
      if (isCustom) {
        const newStandard = iterator.teacher_custom_pacing_plans
          ? iterator.teacher_custom_pacing_plans
          : iterator.custom_global_pacing_plan;
        newStandard['start'] = iterator.start;
        newStandard['end'] = iterator.end;
        newStandard['subject_id'] = iterator.subject_id;
        newStandard['grades_id'] = iterator.grades_id;
        await this.pushStandardToGlobalEvents(newStandard);
      } else {
        let databaseEvent;

        if (this.classroom.isManual) {
          databaseEvent = this.globalPacingPlanEvents?.find((x: any) => x.id === pacingPlanId);
        } else {
          databaseEvent = this.databaseEvents?.find((x: any) => x.id === pacingPlanId);
        }

        if (databaseEvent) {
          const globalStandardOfChoice = structuredClone(databaseEvent);
          globalStandardOfChoice.start = iterator.start;
          globalStandardOfChoice.end = iterator.end;
          await this.pushStandardToGlobalEvents(globalStandardOfChoice);
        } else {
          const newStandard = iterator.pacing_plan;
          newStandard['start'] = iterator.start;
          newStandard['end'] = iterator.end;
          newStandard['subject_id'] = iterator.subject_id;
          newStandard['grades_id'] = iterator.grades_id;
          await this.pushStandardToGlobalEvents(newStandard);
        }
      }
    } catch (error) {
      // console.log(error);
    }
  }

  // for yesterday standard push
  async reteachYesterdayStandard(iterator: any) {
    try {
      // get the previous date from pushed plan
      let index = this.dates.findIndex(
        (x: any) => new Date(x.start).toDateString() === new Date(iterator.start).toDateString(),
      );

      if (index !== -1) {
        for (let i = index - 1; i >= 0; i--) {
          if (this.dates[i].addEvent === true) {
            let previousDate = this.dates[i].start;
            this.globalPacingPlanEvents.map((x: any) => {
              if (new Date(x.start).toDateString() === new Date(previousDate).toDateString()) {
                const data = structuredClone(x);
                data.start = iterator.start;
                data.end = iterator.end;

                this.globalPacingPlanEvents.push(data);
              }

              return x;
            });

            // this.globalPacingPlanEvents.filter((x)=> new Date(x.start).toDateString() ===  new Date(previousDate).toDateString())
            break;
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  // add previous standard to current date
  async addPacingPlanToCalendarPerIndex(iterator: any, index: number) {
    const currentPlan = structuredClone(this.globalPacingPlanEvents[index]);

    currentPlan.start = iterator.start;
    currentPlan.end = iterator.end;
    await this.pushStandardToGlobalEvents(currentPlan);
  }

  // push each pacing plan from date to the next date
  async pushPlansToNextDate(iterator: any) {
    // Filter out the standards from globalPacingPlanEvents that match the iterator condition

    // Filter standards based on the iterator's start and end dates

    let filteredStandards = this.classroom.isManual
      ? this.globalPacingPlanEvents
          .filter(
            (x: any) =>
              new Date(x.start).getTime() >= new Date(iterator.start).getTime() &&
              new Date(x.end).getTime() >= new Date(iterator.end).getTime() &&
              new Date(x.created_at).getTime() < new Date(iterator.created_at).getTime(),
          )
          .sort((a: any, b: any) => {
            return new Date(a.start).getTime() - new Date(b.start).getTime();
          })
      : this.globalPacingPlanEvents
          .filter(
            (x: any) =>
              new Date(x.start).getTime() >= new Date(iterator.start).getTime() &&
              new Date(x.end).getTime() >= new Date(iterator.end).getTime(),
          )
          .sort((a: any, b: any) => {
            return new Date(a.start).getTime() - new Date(b.start).getTime();
          });

    // Group the filtered standards by their start date
    const groupedStandards = filteredStandards.reduce((acc: any, event: any) => {
      const dateKey = new Date(event.start).toDateString(); // Grouping by start date
      if (!acc[dateKey]) {
        acc[dateKey] = []; // Create a new group if it doesn't exist
      }
      acc[dateKey].push(event); // Add the event to the group
      return acc;
    }, {});

    this.globalPacingPlanEvents = this.classroom.isManual
      ? this.globalPacingPlanEvents.filter(
          (x: any) =>
            (new Date(x.start).getTime() < new Date(iterator.start).getTime() &&
              new Date(x.end).getTime() < new Date(iterator.end).getTime()) ||
            new Date(x.created_at).getTime() > new Date(iterator.created_at).getTime(),
        )
      : this.globalPacingPlanEvents.filter(
          (x: any) =>
            new Date(x.start).getTime() < new Date(iterator.start).getTime() &&
            new Date(x.end).getTime() < new Date(iterator.end).getTime(),
        );
    // Convert grouped standards into an array of arrays
    const groupedStandardsArray = Object.values(groupedStandards);

    // Iterate through the grouped standards and adjust their start and end dates
    groupedStandardsArray.forEach((x: any) => {
      const index = this.dates.findIndex(
        (date: any) =>
          date.addEvent === true &&
          date.start.getTime() > new Date(x[0].start).setHours(0, 0, 0, 0) &&
          date.end.getTime() >= new Date(x[0].end).setHours(23, 59, 59, 59),
      );

      if (index !== -1) {
        const ele = this.dates[index];
        if (ele) {
          x.forEach((d: any) => {
            d.start = ele.start;
            d.end = ele.end;
          });
        }
      }
    });

    // Flatten grouped standards into single standards and add them to globalPacingPlanEvents
    groupedStandardsArray.forEach((group: any) => {
      group.forEach((standard: any) => {
        this.globalPacingPlanEvents.push(standard);
      });
    });
  }

  async pushStandardToGlobalEvents(addPushedGlobalPacingPlanEvent: any, multiple?: boolean) {
    if (addPushedGlobalPacingPlanEvent && addPushedGlobalPacingPlanEvent.id) {
      const item = {
        start: addPushedGlobalPacingPlanEvent.start,
        end: addPushedGlobalPacingPlanEvent.end,
        section_id: addPushedGlobalPacingPlanEvent.section_id,
        section_title: addPushedGlobalPacingPlanEvent.section_title,
        grades_id: addPushedGlobalPacingPlanEvent.grades_id,
        subject_id: addPushedGlobalPacingPlanEvent.subject_id,
        id: addPushedGlobalPacingPlanEvent.id,
        is_teacher_custom_pacing_plan: addPushedGlobalPacingPlanEvent.is_teacher_custom_pacing_plan,
      };

      if (multiple) {
        item['multiple'] = true;
      }
      this.globalPacingPlanEvents.push(item);
    }
    // add and retrieve data for auto-classrooms
    if (this.classroom && this.classroom.createdDuringTeacherOnly) {
      const mergeData = [...this.globalPacingPlanEvents, ...this.testAndReviewDays];
      const body = {
        classroom_id: this.classroom.id,
        standards: mergeData,
        //worked changes
      };
      const checkIfClassroomExist = await this.classroomService.getAutoClassroomDataByClassroomId({
        classroom_id: this.classroom.id,
      });
      if (checkIfClassroomExist[0] && checkIfClassroomExist[0].id) {
        await this.classroomService.updateAutoClassroomData(checkIfClassroomExist[0].id, body);
        this.teacherOnlyAutoPopulateData = this.globalPacingPlanEvents;
      } else {
        await this.classroomService.addAutoClassroomDataToDB(body);
        this.teacherOnlyAutoPopulateData = this.globalPacingPlanEvents;
      }
    }
  }

  async getTeacherPacingPlans() {
    try {
      const teacherPacingPlanBody = {
        subject_id: this.classroom.current_subject_id,
        grades_id: this.classroom.grades_id,
        user_id: this.user.id,
        classroom_id: this.classroom?.id,
      };
      return await this.pacingPlanService.getTeacherPacingPlan(teacherPacingPlanBody);
    } catch (error) {
      // console.log(error);
    }
  }

  async setDateToGlobalPacingPlanEvents(pacingPlanEvents) {
    try {
      if (this.classroom.isManual) {
        return this.globalPacingPlanEvents.map((x, i) => {
          (x.start = new Date(pacingPlanEvents[i].start_date)), (x.end = new Date(pacingPlanEvents[i].end_date));
          return x;
        });
      } else {
        // add and retrieve data for auto-classrooms
        // if (this.teacherOnlyAutoPopulateData?.length > 0) {
        // this.globalPacingPlanEvents = this.teacherOnlyAutoPopulateData;
        // } else {
        await this.pacingPlanService.assignDatesToGlobalPacingPlanEvents(this.dates, this.globalPacingPlanEvents);
        // }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async getCurrentCalendar() {
    try {
      if (this.classroom && this.classroom.createdDuringTeacherOnly && !this.isTeacherOnly) {
        // added the dummy calendar with teacher only start and end dates
        this.currentCalendar = [
          {
            acceleration: [],
            bank_days: [],
            created_at: 10000,
            end: this.user.profileData.teacherOnlyEndDate,
            holidays: [],
            id: 0,
            instructional_days: 2,
            pupil_free: [],
            school_id: 0,
            start: this.user.profileData.teacherOnlyStartDate,
            unassigned: [],
          },
        ];
      } else {
        const selectedSchoolCalendar = this.storageService.getItem('selectedSchoolCalendarByAdmin');

        this.currentCalendar =
          this.schoolCalendarEvents && this.schoolCalendarEvents.length > 0
            ? this.schoolCalendarEvents
            : ((await this.getSchoolCalendarBySchoolId()) as any[]);

        this.currentCalendar = this.currentCalendar.filter((x) => x.id === selectedSchoolCalendar.id);
      }

      if (this.user.is_trial_user) {
        this.currentCalendar[0].start = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        this.currentCalendar[0].end = this.getLastDayOfMonthOneYearFromNow(new Date());

        await this.setEventsAsCurrentYearEvents();
      }

      this.events = await this.updateEventsData();
    } catch (error) {
      // console.log(error);
    }
  }

  async setEventsAsCurrentYearEvents() {
    this.currentCalendar[0].holidays.map((x) => {
      x.end = new Date(x.end).setFullYear(new Date().getFullYear());
      x.start = new Date(x.start).setFullYear(new Date().getFullYear());
    });

    this.currentCalendar[0].acceleration.map((x) => {
      x.end = new Date(x.end).setFullYear(new Date().getFullYear());
      x.start = new Date(x.start).setFullYear(new Date().getFullYear());
    });

    this.currentCalendar[0].pupil_free.map((x) => {
      x.end = new Date(x.end).setFullYear(new Date().getFullYear());
      x.start = new Date(x.start).setFullYear(new Date().getFullYear());
    });

    this.currentCalendar[0].bank_days.map((x) => {
      x.end = new Date(x.end).setFullYear(new Date().getFullYear());
      x.start = new Date(x.start).setFullYear(new Date().getFullYear());
    });

    this.currentCalendar[0].unassigned.map((x) => {
      x.end = new Date(x.end).setFullYear(new Date().getFullYear());
      x.start = new Date(x.start).setFullYear(new Date().getFullYear());
    });
  }

  getLastDayOfMonthOneYearFromNow(currentDate: Date) {
    const nextYear = new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), 1);

    nextYear.setMonth(nextYear.getMonth() + 1);
    nextYear.setDate(0);

    return nextYear;
  }

  async closeDialog() {
    try {
      this.analyticsService?.trackEvent('Pacing Plan Dialog', 'Pacing Plan Month View');
      this.closeDialogModal.emit(true);
    } catch (error) {
      // console.log(error);
    }
  }

  closeOpenMonthViewDay() {
    this.activeDayIsOpen = false;
  }

  async getSchoolCalendarBySchoolId() {
    try {
      const params = {
        school_id: this.user.school_id,
      };
      return await this.calendarService.schoolCalendarBySchoolId(params);
    } catch (error) {
      // console.log(error);
    }
  }

  async updateEventsData() {
    try {
      const events = [];
      this.currentCalendar[0].holidays.map((event) =>
        events.push({
          start: new Date(event.start),
          end: new Date(event.end),
          title: event.title,
          cssClass: HOLIDAY_EVENT[0].cssClass,
        }),
      ),
        this.currentCalendar[0].pupil_free.map((event) =>
          events.push({
            start: new Date(event.start),
            end: new Date(event.end),
            title: event.title,
            cssClass: PUPIL_EVENT[0].cssClass,
          }),
        ),
        this.currentCalendar[0].acceleration.map((event) =>
          events.push({
            start: new Date(event.start),
            end: new Date(event.end),
            title: event.title,
            cssClass: ACCELERATION_EVENT[0].cssClass,
          }),
        );

      this.currentCalendar[0].unassigned.map((event) =>
        events.push({
          start: new Date(event.start),
          end: new Date(event.end),
          title: event.title,
          cssClass: UNASSIGNED_EVENT[0].cssClass,
        }),
      );

      return events;
    } catch (error) {
      // console.log(error);
    }
  }

  // async beforeMonthViewRender({ body }: { body: CalendarMonthViewDay[] }) {
  //   try {
  //     if (this.classroom.isManual) {
  //       body.forEach(async (day: any) => {
  //         if (this.teacherLessonPlanEvents && this.teacherLessonPlanEvents.length > 0) {
  //           const isTeacherLessonPlan = this.teacherLessonPlanEvents.some(
  //             (x) => new Date(x.start).toDateString() === day.date.toDateString(),
  //           );
  //           day['isLessonPlan'] = isTeacherLessonPlan ? true : false;
  //         }
  //         if (this.globalPacingPlanEvents && this.globalPacingPlanEvents?.length > 0) {
  //           const filteredEvents = this.globalPacingPlanEvents.filter((x: any) => {
  //             return (
  //               new Date(x?.start).toDateString() === day.date.toDateString() &&
  //               new Date(x?.end).toDateString() === day.date.toDateString()
  //             );
  //           });

  //           if (filteredEvents?.length > 0) {
  //             if (filteredEvents?.length > 1) {
  //               day.isMultiple = true;
  //             }
  //             // day.events.push(
  //             //   filteredEvents[0].global_pacing_plan
  //             //     ? filteredEvents[0].global_pacing_plan
  //             //     : filteredEvents[0].teacher_custom_pacing_plans
  //             // );
  //             day.events.push(filteredEvents[0]);
  //           }
  //         }

  //         // if (this.user.isTeacherOnlyOnBoarded && this.classroom.isManual) {
  //         //   if (this.updatedPacingPlanEvents && this.updatedPacingPlanEvents.length > 0) {
  //         //     this.updatedTestAndReviewDays.filter(async (x: any) => {
  //         //       const startDate = new Date(x.start);
  //         //       const endDate = new Date(x.end);
  //         //       if (
  //         //         startDate.toDateString() === day.date.toDateString() &&
  //         //         endDate.toDateString() === day.date.toDateString()
  //         //       ) {
  //         //         const eventDay: any = {
  //         //           section_id: x.type === 'review-day' ? 'Review Day' : 'Test Day',
  //         //           section_title: '',
  //         //         };
  //         //         day.events.push(eventDay);
  //         //       }
  //         //     });
  //         //   }
  //         // } else {
  //         if (this.databaseEvents && this.databaseEvents.custom_pacing_plans_test_review_days?.length > 0) {
  //           this.databaseEvents.custom_pacing_plans_test_review_days.filter(async (x: any) => {
  //             const startDate = new Date(x.start_date);
  //             const endDate = new Date(x.end_date);
  //             if (
  //               startDate.toDateString() === day.date.toDateString() &&
  //               endDate.toDateString() === day.date.toDateString()
  //             ) {
  //               const eventDay: any = {
  //                 section_id: x.day === 'review-day' ? 'Review Day' : 'Test Day',
  //                 section_title: '',
  //               };
  //               day.events.push(eventDay);
  //             }
  //           });
  //         }
  //         // }

  //         if (!this.user?.user?.is_trial_user) {
  //           day.cssClass =
  //             day.events[0] && day.events[0].cssClass
  //               ? day.events[0].cssClass
  //               : day?.date.getTime() < new Date(this.schoolCalendarEvents[0]?.start).setHours(0, 0, 0, 0)
  //                 ? 'cal-disabled'
  //                 : '';
  //         }
  //       });
  //     } else {
  //       body.forEach(async (day: any) => {
  //         // await this.removeDeletedDaysFromTestandReviewDay();
  //         const ifCurrentDateIsTestAndReviewDay = await this.ifDayIsTestOrReviewDay(day.date);

  //         if (this.teacherLessonPlanEvents && this.teacherLessonPlanEvents.length > 0) {
  //           const isTeacherLessonPlan = this.teacherLessonPlanEvents.some(
  //             (x) => new Date(x.start).toDateString() === day.date.toDateString(),
  //           );
  //           day['isLessonPlan'] = isTeacherLessonPlan ? true : false;
  //         }
  //         if (ifCurrentDateIsTestAndReviewDay && ifCurrentDateIsTestAndReviewDay.length > 0) {
  //           const x: any = {
  //             section_id: ifCurrentDateIsTestAndReviewDay[0].type,
  //             section_title: '',
  //           };
  //           day.events.push(x);
  //         } else {
  //           if (this.globalPacingPlanEvents && this.globalPacingPlanEvents.length > 0) {
  //             this.globalPacingPlanEvents.map((event) => {
  //               if (event.teacher_custom_pacing_plans_id && event.teacher_custom_pacing_plans_id !== 0) {
  //                 event['section_title'] = event.teacher_custom_pacing_plans.section_title;
  //               }
  //               return event;
  //             });
  //             const filteredEvents = this.globalPacingPlanEvents.filter(
  //               (x: any) =>
  //                 new Date(x?.start).toDateString() === day.date.toDateString() &&
  //                 new Date(x?.end).toDateString() === day.date.toDateString(),
  //             );

  //             if (filteredEvents?.length > 0) {
  //               if (filteredEvents?.length > 1) {
  //                 day.isMultiple = true;
  //               }
  //               day.events.push(filteredEvents[0]);
  //             }
  //           }
  //         }

  //         if (!this.user.is_trial_user) {
  //           day.cssClass =
  //             day.events[0] && day.events[0].cssClass
  //               ? day.events[0].cssClass
  //               : day?.date.getTime() < new Date(this.schoolCalendarEvents[0]?.start).setHours(0, 0, 0, 0)
  //                 ? 'cal-disabled'
  //                 : '';
  //         }
  //       });
  //     }
  //   } catch (error) {
  //     console.log(error);
  //   }
  // }

  beforeMonthViewRender({ body }: { body: CalendarMonthViewDay[] }): void {
    try {
      if (this.classroom.isManual) {
        body.forEach(async (day: any) => {
          await this.disabledCalendarDays(day);

          if (this.teacherLessonPlanEvents && this.teacherLessonPlanEvents.length > 0) {
            const isTeacherLessonPlan = this.teacherLessonPlanEvents.some(
              (x) => new Date(x.start).toDateString() === day.date.toDateString(),
            );
            day['isLessonPlan'] = isTeacherLessonPlan ? true : false;
          }

          if (this.globalPacingPlanEvents && this.globalPacingPlanEvents?.length > 0) {
            const currentSubjectId = this.classroom.current_subject_id;

            // Updated the subject_id with currentSubjectId because browse Standards From Different Subjects then it creates issue
            this.globalPacingPlanEvents = this.globalPacingPlanEvents.map((event: any) => {
              if (event.subject_id !== currentSubjectId) {
                return { ...event, subject_id: currentSubjectId };
              }
              return event;
            });

            const filteredEvents = this.globalPacingPlanEvents.filter((x: any) => {
              return (
                new Date(x?.start).toDateString() === day.date.toDateString() &&
                new Date(x?.end).toDateString() === day.date.toDateString() &&
                (x.subject_id === currentSubjectId || x.subjectId === currentSubjectId)
              );
            });

            if (filteredEvents?.length > 0) {
              if (filteredEvents?.length > 1) {
                day.isMultiple = true;
              }
              // day.events.push(
              //   filteredEvents[0].global_pacing_plan
              //     ? filteredEvents[0].global_pacing_plan
              //     : filteredEvents[0].teacher_custom_pacing_plans
              // );

              day.events.push(filteredEvents[0]);
            }
          }

          // ** Old code **
          // if (this.databaseEvents && this.databaseEvents.custom_pacing_plans_test_review_days?.length > 0) {
          //   const currentSubjectId = this.classroom.current_subject_id;
          //   this.databaseEvents.custom_pacing_plans_test_review_days.filter(async (x: any) => {
          //     const startDate = new Date(x.start_date);
          //     const endDate = new Date(x.end_date);
          //     if (
          //       startDate.toDateString() === day.date.toDateString() &&
          //       endDate.toDateString() === day.date.toDateString()
          //     ) {
          //       const eventDay: any = {
          //         section_id: x.day === 'review-day' ? 'Review Day' : 'Test Day',
          //         section_title: '',
          //       };
          //       day.events.push(eventDay);
          //     }
          //   });
          // }

          const currentAddedTestAndReviewDays = this.storageService.getItem('currentlyAddedTestAndReviewDays') || [];
          if (
            !this.databaseEvents?.custom_pacing_plans_test_review_days?.length &&
            !currentAddedTestAndReviewDays.length
          ) {
            return;
          }

          const currentSubjectId = this.classroom.current_subject_id;
          const combinedDays = [
            ...(this.databaseEvents?.custom_pacing_plans_test_review_days || []),
            ...(currentAddedTestAndReviewDays || []),
          ];

          combinedDays.forEach((x: any) => {
            const startDate = new Date(x.start_date || x.start);
            const endDate = new Date(x.end_date || x.end);

            if (
              startDate.toDateString() === day.date.toDateString() &&
              endDate.toDateString() === day.date.toDateString() &&
              (x.subject_id || x.subjectId) === currentSubjectId
            ) {
              const eventDay: any = {
                section_id: x.day === 'review-day' ? 'Review Day' : 'Test Day',
                section_title: '',
              };
              day.events.push(eventDay);
            }
          });

          // if (
          //   !this.user?.is_trial_user ||
          //   this.user?.is_trial_user === null ||
          //   this.user?.is_trial_user === undefined
          // ) {
          //   day.cssClass =
          //     day.events[0] && day.events[0].cssClass
          //       ? day.events[0].cssClass
          //       : day?.date.getTime() < new Date(this.currentCalendar[0]?.start).setHours(0, 0, 0, 0)
          //         ? 'cal-disabled'
          //         : '';
          // }
        });
      } else {
        body.forEach(async (day: any) => {
          // await this.removeDeletedDaysFromTestandReviewDay();

          await this.disabledCalendarDays(day);

          if (this.deletedDays && this.deletedDays.length > 0) {
            await this.filterTestAndReviewDayData(this.deletedDays);
            // this.filterTestAndReviewDayData(this.deletedReviewDays);
          }

          const ifCurrentDateIsTestAndReviewDay = await this.ifDayIsTestOrReviewDay(day.date);

          if (this.teacherLessonPlanEvents && this.teacherLessonPlanEvents.length > 0) {
            const isTeacherLessonPlan = this.teacherLessonPlanEvents.some(
              (x) => new Date(x.start).toDateString() === day.date.toDateString(),
            );
            day['isLessonPlan'] = isTeacherLessonPlan ? true : false;
          }

          if (ifCurrentDateIsTestAndReviewDay && ifCurrentDateIsTestAndReviewDay.length > 0) {
            const x: any = {
              section_id: ifCurrentDateIsTestAndReviewDay[0].type,
              section_title: '',
            };
            day.events.push(x);
          } else {
            if (this.globalPacingPlanEvents && this.globalPacingPlanEvents.length > 0) {
              const currentSubjectId = this.classroom.current_subject_id;

              this.globalPacingPlanEvents.map((event) => {
                if (event.teacher_custom_pacing_plans_id && event.teacher_custom_pacing_plans_id !== 0) {
                  event['section_title'] = event.teacher_custom_pacing_plans.section_title;
                }
                return event;
              });
              const filteredEvents = this.globalPacingPlanEvents.filter(
                (x: any) =>
                  new Date(x?.start).toDateString() === day.date.toDateString() &&
                  new Date(x?.end).toDateString() === day.date.toDateString() &&
                  (x.subject_id === currentSubjectId || x.subjectId === currentSubjectId),
              );

              if (filteredEvents?.length > 0) {
                if (filteredEvents?.length > 1) {
                  day.isMultiple = true;
                }
                day.events.push(filteredEvents[0]);
              }
            }
          }

          // if (!this.user.is_trial_user) {
          //   day.cssClass =
          //     day.events[0] && day.events[0].cssClass
          //       ? day.events[0].cssClass
          //       : day?.date.getTime() < new Date(this.currentCalendar[0]?.start).setHours(0, 0, 0, 0)
          //         ? 'cal-disabled'
          //         : '';
          // }
        });
      }
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * @description Disables calendar days based on certain conditions.
   *  Set CSS class for non-trial users
   *  Disable past dates before the school start date.
   * @returns {Promise<void>}
   * @param day
   */
  // private async disabledCalendarDays(day: any): Promise<void> {
  //   try {
  //     if (!this?.user?.is_trial_user || this.user?.is_trial_user === null || this.user?.is_trial_user === undefined) {
  //       day.cssClass =
  //         day.events[0] && day.events[0].cssClass
  //           ? day.events[0].cssClass
  //           : day?.date.getTime() < new Date(this.currentCalendar[0]?.start).setHours(0, 0, 0, 0)
  //             ? 'cal-disabled'
  //             : '';
  //     }
  //   } catch (error) {
  //     console.log(error);
  //   }
  // }

  /**
   * @description Disables calendar days based on certain conditions.
   *  Set CSS class for non-trial users
   *  Disable past dates before the school start date.
   * @returns {Promise<void>}
   * @param day
   */
  private async disabledCalendarDays(day: any): Promise<void> {
    try {
      const isTrialUser = this?.user?.is_trial_user;

      // Apply disabling logic only if not trial user
      if (!isTrialUser || isTrialUser === null || isTrialUser === undefined) {
        const isWeekend = day.day === 0 || day.day === 6;

        // Disable only non-weekend days before allowed calendar start
        if (!isWeekend && day.date.getTime() < new Date(this.currentCalendar[0]?.start).setHours(0, 0, 0, 0)) {
          day.cssClass = day.events[0] && day.events[0].cssClass ? day.events[0].cssClass : 'cal-disabled';
        } else {
          // Clear cssClass if not disabled
          day.cssClass = day.events[0]?.cssClass || '';
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async openDayView(day) {
    try {
      this.standardsListPopoverTemplate.hide();
      this.analyticsService?.trackEvent('Day View', 'Pacing Plan Month View');
      if (day.events && day.events.length === 0) {
        await this.renderToDayView(day);
      } else {
        if ((day.events[0] && day.events[0].section_id === 'Review Day') || day.events[0].section_id === 'Test Day') {
          this.toasterService.showToast(`We have a ${day.events[0].section_id} on this day`, 'info');
        } else {
          await this.renderToDayView(day);
        }
      }
    } catch (error) {
      // console.log(error);
    }
  }

  async renderToDayView(day) {
    try {
      this.analyticsService?.trackEvent('Render To Day View', 'Pacing Plan Month View');
      this.pacingPlanService.removeObservers();
      if (this.isTeacherCalendar && this.isTeacherCalendar.id) {
        this.pacingPlanService.view = 'Day';
        this.calendarService.viewDate = day.date;
        this.pacingPlanService.grades_id = this.isTeacherCalendar.grades_id;
        this.pacingPlanService.subject_id = this.isTeacherCalendar.subject_id;
        this.router.navigate(['/calendar/teacher-calender', true, this.isTeacherCalendar.id]);
      } else if (this.classroom && this.classroom.id) {
        this.pacingPlanService.view = 'Day';
        this.calendarService.viewDate = day.date;
        this.pacingPlanService.grades_id = this.classroom.grades_id;
        this.pacingPlanService.subject_id = this.classroom.current_subject_id;
        this.pacingPlanService.currentUrl = this.router.url;

        this.pacingPlanService.isLessonPan.next(true);

        this.isModal ? this.pacingPlanService.isExpandView.emit(true) : this.pacingPlanService.isExpandView.emit(false);
      } else {
        this.pacingPlanService.grades_id = day.events.length > 0 ? day.events[0].grades_id : 0;
        this.pacingPlanService.subject_id = day.events.length > 0 ? day.events[0].subject_id : 0;
        this.currentDate.emit(day.date);
        this.newItemEvent.emit('Day');
      }
    } catch (error) {
      // console.log(error);
    }
  }

  navigateCalendar() {
    this.analyticsService?.trackEvent('Calendar View', 'Pacing Plan Month View');
    if (this.user && this.user.role_id !== ROLES.SUPER_ADMIN) {
      const selectedSchoolCalendar = this.storageService.getItem('selectedSchoolCalendarByAdmin');

      if (Array.isArray(this.currentCalendar) && this.currentCalendar) {
        // Find the calendar in currentCalendar that matches selected School Calendar
        this.currentSelected = this.currentCalendar.filter((x) => x.id === selectedSchoolCalendar.id);
      }

      const [calStart] = this.currentSelected,
        currentCalendarStartDate = calStart ? new Date(calStart.start) : null,
        currentCalendarEndDate = calStart ? new Date(calStart.end) : null;

      this.isCalendarStart =
        currentCalendarStartDate &&
        currentCalendarStartDate.getMonth() + ',' + currentCalendarStartDate.getFullYear() ===
          this.viewDate.getMonth() + ',' + this.viewDate.getFullYear();
      this.isCalendarEnd =
        currentCalendarEndDate &&
        currentCalendarEndDate.getMonth() + ',' + currentCalendarEndDate.getFullYear() ===
          this.viewDate.getMonth() + ',' + this.viewDate.getFullYear();

      if (!this.isCalendarStart && !this.isCalendarEnd) {
        this.isCalendarStart = false;
        this.isCalendarEnd = false;
      }
    }
  }

  async getDates() {
    try {
      this.dates = [];
      const date1 = this.user?.role_id === 19 ? dayjs().startOf('year') : dayjs(this.currentCalendar[0].start);
      const date2 =
        this.user?.role_id === 19 ? dayjs(dayjs().add(1, 'year')).endOf('year') : dayjs(this.currentCalendar[0].end);
      const diff = date2.diff(date1, 'day');
      const calendarStartDate =
        this.user?.role_id === 19
          ? dayjs().startOf('year').add(1, 'day')
          : this.user?.is_trial_user
            ? dayjs(this.currentCalendar[0].start)
            : dayjs(this.currentCalendar[0].start);
      for (let i = 0; i < diff; i++) {
        const startDate: any = dayjs(calendarStartDate).add(i, 'day');

        const date: any = {
          start: dayjs(startDate.startOf('day'), 'YYYY-MM-DD').tz().toDate(),
          end: dayjs(startDate.endOf('day'), 'YYYY-MM-DD').tz().toDate(),
          addEvent: await this.checkIfDateIsValid(startDate),
        };
        this.dates.push(date);
      }
    } catch (error) {
      console.log('getDates', error);
    }
  }

  async checkIfDateIsValid(startDate: any) {
    try {
      if (startDate.day() === 0 || startDate.day() === 6) {
        return false;
      }

      const d = this.checkIfDeletedTestAndReviewDay(new Date(startDate.$d.setHours(0, 0, 0, 0)));

      if (d !== undefined) {
        const removalDay = this.testAndReviewDays.find((x) => x.start.toDateString() === startDate.$d.toDateString());
        if (removalDay) {
          this.testAndReviewDays.splice(this.testAndReviewDays.indexOf(removalDay), 1);
        }
        return d;
      }

      const ifCurrentDateIsTestAndReviewDay = await this.ifDayIsTestOrReviewDay(
        new Date(startDate.$d.setHours(0, 0, 0, 0)),
      );

      if (ifCurrentDateIsTestAndReviewDay && ifCurrentDateIsTestAndReviewDay.length > 0) {
        return false;
      }

      if (this.checkIfDateIsEventDate(startDate?.$d)) {
        return false;
      }

      if (this.checkIfPresentInExtraEvents(startDate)) {
        return true;
      }

      return false;
    } catch (error) {
      // console.log(error)
    }
  }

  checkIfDeletedTestAndReviewDay(date: any) {
    if (this.deletedDays.length === 0) {
      return;
    }

    const res = this.deletedDays.find((x) => new Date(x.start).toDateString() === new Date(date).toDateString());

    if (!res) {
      return;
    }

    return res.added_event ? true : false;
  }

  checkIfDateIsEventDate(date: Date) {
    try {
      return this.events.some(
        (x) =>
          date.setHours(0, 0, 0, 0) >= new Date(x.start).setHours(0, 0, 0, 0) &&
          date.getTime() <= new Date(x.end).setHours(23, 59, 59, 59),
      );
    } catch (error) {
      // console.log(error);
    }
  }

  // async getTestAndReviewDays() {
  //   try {
  //     const date1 = dayjs(this.currentCalendar[0].start);
  //     const date2 = dayjs(this.currentCalendar[0].end);
  //     const diff = date2.diff(date1, 'day');

  //     const dates = [];
  //     this.testAndReviewDays = [];

  //     if (this.classroom.isManual) {
  //       this.databaseEvents.custom_pacing_plans_test_review_days.map((x: any) => {
  //         this.testAndReviewDays.push({
  //           start: new Date(x.start_date),
  //           end: new Date(x.end_date),
  //           isDay: true,
  //           type: x.day === 'test-day' ? 'Test Day' : 'Review Day',
  //         });

  //         return x;
  //       });
  //       return;
  //     }

  //     for (let i = 0; i < diff; i++) {
  //       const startDate: any = dayjs(new Date(this.currentCalendar[0].start))
  //         .year(new Date(this.currentCalendar[0].start).getFullYear())
  //         .add(i, 'day');
  //       const date: any = {
  //         start: dayjs(startDate, 'YYYY-MM-DD').tz().toDate(),
  //         end: dayjs(startDate.endOf('day'), 'YYYY-MM-DD').tz().toDate(),
  //         isDay:
  //           startDate.day() === 0 || startDate.day() === 6
  //             ? false
  //             : !this.events.find((event) => {
  //                   if (
  //                     startDate.startOf('day').valueOf() >= new Date(event.start).getTime() &&
  //                     startDate.startOf('day').valueOf() <= new Date(event.end).getTime()
  //                   ) {
  //                     return event;
  //                   }
  //                 })
  //               ? this.checkIfPresentInExtraEvents(startDate)
  //               : false,
  //       };
  //       if (date.isDay) {
  //         dates.push(date);
  //       }
  //     }

  //     const testDay = this.classroom?.test_day ? this.classroom?.test_day : this.user?.teachingInfo.examPerdays;

  //     let newIndex = testDay;

  //     const checkAlreadyDeletedEvents = await this.pacingPlanService.checkedAlreadyDeletedEvents({
  //       user_id: this.user?.id,
  //       subject_id: this.classroom.current_subject_id,
  //       classroom_id: this.classroom.id,
  //     });
  //     this.deletedDays = checkAlreadyDeletedEvents;

  //     this.deletedTestDays = checkAlreadyDeletedEvents.filter((event: any) => event.type === 'Test Day');

  //     this.deletedReviewDays = checkAlreadyDeletedEvents.filter((event: any) => event.type === 'Review Day');

  //     dates.map((event) => {
  //       const testDayValues = dates[newIndex];

  //       const reviewDayValues = dates[newIndex + 1];

  //       if (testDayValues) {
  //         this.filterTestAndReviewDayData(this.deletedTestDays);
  //         this.testAndReviewDays.push({
  //           start: testDayValues.start,
  //           end: testDayValues.end,
  //           isDay: testDayValues.isDay,
  //           type: 'Test Day',
  //         });
  //         // }
  //       }

  //       if (reviewDayValues) {
  //         this.filterTestAndReviewDayData(this.deletedReviewDays);
  //         this.testAndReviewDays.push({
  //           start: reviewDayValues.start,
  //           end: reviewDayValues.end,
  //           isDay: reviewDayValues.isDay,
  //           type: 'Review Day',
  //         });
  //         // }}
  //       }

  //       newIndex = newIndex + (testDay + 2);
  //       return event;
  //     });

  //     // this.dates = dates;
  //   } catch (error) {
  //     console.log('getTestAndReviewDays', error);
  //   }
  // }

  async getTestAndReviewDays() {
    try {
      // Made the start and end date optional chaining to avoid error
      const date1 = dayjs(this.currentCalendar?.[0]?.start);
      const date2 = dayjs(this.currentCalendar?.[0]?.end);
      const diff = date2.diff(date1, 'day');

      const dates = [];
      this.testAndReviewDays = [];

      if (this.classroom.isManual) {
        // const currentSubjectId = this.classroom.current_subject_id;
        // this.databaseEvents.custom_pacing_plans_test_review_days
        //   .filter((day: any) => (day.subject_id || day.subjectId) === currentSubjectId)
        //   .forEach((x: any) => {
        //     this.testAndReviewDays.push({
        //       start: new Date(x.start_date),
        //       end: new Date(x.end_date),
        //       isDay: true,
        //       type: x.day === 'test-day' ? 'Test Day' : 'Review Day',
        //     });
        //   });

        const currentSubjectId = this.classroom.current_subject_id;
        const reviewDays = this.databaseEvents?.custom_pacing_plans_test_review_days;

        if (Array.isArray(reviewDays)) {
          reviewDays
            .filter((day: any) => (day.subject_id || day.subjectId) === currentSubjectId)
            .forEach((x: any) => {
              this.testAndReviewDays.push({
                start: new Date(x.start_date),
                end: new Date(x.end_date),
                isDay: true,
                type: x.day === 'test-day' ? 'Test Day' : 'Review Day',
              });
            });
        }

        return;
      }

      for (let i = 0; i < diff; i++) {
        const startDate: any = dayjs(new Date(this.currentCalendar[0].start))
          .year(new Date(this.currentCalendar[0].start).getFullYear())
          .add(i, 'day');
        const date: any = {
          start: dayjs(startDate, 'YYYY-MM-DD').tz().toDate(),
          end: dayjs(startDate.endOf('day'), 'YYYY-MM-DD').tz().toDate(),
          isDay:
            startDate.day() === 0 || startDate.day() === 6
              ? false
              : !this.events.find((event) => {
                    if (
                      startDate.startOf('day').valueOf() >= new Date(event.start).getTime() &&
                      startDate.startOf('day').valueOf() <= new Date(event.end).getTime()
                    ) {
                      return event;
                    }
                  })
                ? this.checkIfPresentInExtraEvents(startDate)
                : false,
        };
        if (date.isDay) {
          dates.push(date);
        }
      }

      const testDay = this.classroom?.test_day ? this.classroom?.test_day : this.user.user?.teachingInfo.examPerdays;

      let newIndex = testDay;

      const checkAlreadyDeletedEvents = await this.pacingPlanService.checkedAlreadyDeletedEvents({
        user_id: this.user?.id,
        subject_id: this.classroom.current_subject_id,
        classroom_id: this.classroom.id,
      });

      this.deletedDays = checkAlreadyDeletedEvents;

      this.deletedTestDays = checkAlreadyDeletedEvents.filter((event: any) => event.type === 'Test Day');

      this.deletedReviewDays = checkAlreadyDeletedEvents.filter((event: any) => event.type === 'Review Day');

      // dates.map(async (event) => {
      //   const testDayValues = dates[newIndex];
      //   const reviewDayValues = dates[newIndex + 1];

      //   if (testDayValues) {
      //     await this.filterTestAndReviewDayData(this.deletedTestDays);
      //     this.testAndReviewDays.push({
      //       start: testDayValues.start,
      //       end: testDayValues.end,
      //       isDay: testDayValues.isDay,
      //       type: 'Test Day',
      //     });
      //   }

      //   if (reviewDayValues) {
      //     await this.filterTestAndReviewDayData(this.deletedReviewDays);
      //     this.testAndReviewDays.push({
      //       start: reviewDayValues.start,
      //       end: reviewDayValues.end,
      //       isDay: reviewDayValues.isDay,
      //       type: 'Review Day',
      //     });
      //   }

      //   newIndex = newIndex + (testDay + 2);
      //   return event;
      // });

      for (; newIndex < dates.length; ) {
        const testDayValues = dates[newIndex];
        const reviewDayValues = dates[newIndex + 1];

        if (testDayValues) {
          await this.filterTestAndReviewDayData(this.deletedTestDays);
          this.testAndReviewDays.push({
            start: testDayValues.start,
            end: testDayValues.end,
            isDay: testDayValues.isDay,
            type: 'Test Day',
          });
        }

        if (reviewDayValues) {
          await this.filterTestAndReviewDayData(this.deletedReviewDays);
          this.testAndReviewDays.push({
            start: reviewDayValues.start,
            end: reviewDayValues.end,
            isDay: reviewDayValues.isDay,
            type: 'Review Day',
          });
        }

        newIndex = newIndex + (testDay + 2); // assuming testDay is defined elsewhere
      }

      // this.dates = dates;
    } catch (error) {
      console.log(error);
    }
  }

  async filterTestAndReviewDayData(deleteDayArray: any) {
    const deleteCriteria = deleteDayArray.map((item) => ({
      type: item.type,
      start: item.start,
    }));
    this.testAndReviewDays = this.testAndReviewDays.filter((item) => {
      return !deleteCriteria.some((delItem: any) => {
        return delItem.type === item.type && new Date(delItem.start).toString() === item.start.toString();
      });
    });
  }

  checkIfPresentInExtraEvents(startDate: any) {
    if (this.classroom?.extras?.eventDates?.length === 0 || this.classroom?.subject_id?.length > 1) {
      // added the condition for teacher only portal
      if (this.isTeacherOnly) {
        return this.checkIfDateIsPresentInSubjectHoursData(startDate.format('dddd'));
      } else {
        // for multi subjects classrooms
        if (this.classroom.createdDuringTeacherOnly) {
          return this.checkIfDateIsPresentInSubjectHoursData(startDate.format('dddd'));
        } else {
          let filteredData;
          if (this.classroom?.subject_id?.length === 1) {
            filteredData = this.subjectHoursData.find(
              (x) =>
                x.day.toLowerCase() === startDate.format('dddd').toLowerCase() &&
                x.subject_id === this.classroom.current_subject_id &&
                x.grades_id === this.classroom.grades_id,
            )
              ? true
              : false;
          } else {
            filteredData = this.classroom?.extras?.eventDates?.find(
              (x) =>
                x.day.toLowerCase() === startDate.format('dddd').toLowerCase() &&
                x.subject_id === this.classroom.current_subject_id,
            )
              ? true
              : false;
          }
          return filteredData;
        }
      }
    }
    return this.checkIfDateisPresentInSubjectHours(startDate.format('dddd'));
  }

  checkIfDateisPresentInSubjectHours(dayString: string) {
    if (this.classroom?.extras?.eventDates?.length > 0) {
      return this.classroom?.extras?.eventDates.find((x) => x.day.toLowerCase() === dayString.toLowerCase())
        ? true
        : false;
    }

    return false;
  }

  /**
   * @description Method is used to check if the day is in the schedule for teacher only users
   * @param dayString
   * @returns boolean
   */
  checkIfDateIsPresentInSubjectHoursData(dayString: string): boolean {
    if (this.classroom?.extras?.eventDates?.length > 0) {
      return this.classroom?.extras?.eventDates.find(
        (x) =>
          x.day.toLowerCase() === dayString.toLowerCase() &&
          (x.subjectId || x.subject_id) === this.classroom.current_subject_id,
      )
        ? true
        : false;
    }

    return false;
  }

  async ifDayIsTestOrReviewDay(date: Date) {
    try {
      if (this.testAndReviewDays.length > 0) {
        return this.testAndReviewDays.filter(
          (event) => new Date(event.start.setHours(0, 0, 0, 0)).getTime() === date.getTime(),
        );
      } else {
        return [];
      }
    } catch (error) {
      // console.log(error);
    }
  }

  async removeDeletedDaysFromTestandReviewDay() {
    this.testAndReviewDays = this.testAndReviewDays.filter((x) => {
      return !this.deletedDays.some((deletedDay) => {
        const deletedStart = new Date(deletedDay.start || deletedDay.start_date).toDateString();
        const xStart = new Date(x.start || x.start_date).toDateString();
        return deletedStart === xStart;
      });
    });
  }

  standardsForTheDay(date: Date) {
    try {
      return this.globalPacingPlanEvents.filter(
        (x: any) =>
          new Date(x?.start).toDateString() === date.toDateString() &&
          new Date(x?.end).toDateString() === date.toDateString(),
      );
    } catch (error) {
      console.log(error);
    }
  }

  async removeDeletedDaysFromTestAndReviewDayForManual(): Promise<void> {
    if (
      this.databaseEvents?.custom_pacing_plans_test_review_days &&
      this.databaseEvents.custom_pacing_plans_test_review_days.length > 0
    ) {
      this.databaseEvents.custom_pacing_plans_test_review_days =
        this.databaseEvents.custom_pacing_plans_test_review_days.filter((testReviewDay) => {
          const testReviewStart = new Date(testReviewDay.start || testReviewDay.start_date).toDateString();
          return !this.deletedDays.some((deletedDay) => {
            const deletedStart = new Date(deletedDay.start || deletedDay.start_date).toDateString();
            return testReviewStart === deletedStart;
          });
        });
    }
  }
}
